import { T, useTranslation } from "@redotech/redo-i18n";
import ClockIcon from "@redotech/redo-icons/clock.svg";
import CoinsIcon from "@redotech/redo-icons/coins-01.svg";
import ShieldIcon from "@redotech/redo-icons/shield-tick.svg";
import { Flex } from "@redotech/redo-web/flex";
import { assertNever } from "@redotech/util/type";
import { memo } from "react";
import type {
  FormState,
  MerchantType,
  StepId,
  ValidationErrors,
  WithdrawalsOnboardingAddressOption,
} from "./withdrawals-onboarding-modal";
import * as styles from "./withdrawals-onboarding-modal.module.css";
import {
  BillingSection,
  Callout,
  CheckList,
  ChoiceCard,
  ConfirmationFieldLink,
  CoverageCard,
  FieldCard,
  FulfillmentNotifySection,
  FulfillmentStates,
  NoteCard,
  NotificationsSection,
  NumberedSteps,
  PolicySection,
  ShippingSection,
  StepHeading,
  SubHeading,
  VimeoVideo,
  WithdrawalLinkSection,
} from "./withdrawals-onboarding-sections";
import { buildPolicy } from "./withdrawals-policy";

const WITHDRAWALS_EXPLAINER_VIDEO_URL =
  "https://player.vimeo.com/video/1202351057?autoplay=1&title=0&byline=0&portrait=0";

export const StepContent = memo(function StepContent({
  billingChecked,
  billingCheckPending,
  billingSettingsHref,
  form,
  merchant,
  onCheckBilling,
  onSelectMerchant,
  notificationSettingsHref,
  returnAddressOptions,
  addressOptionsError,
  shippingSettingsHref,
  portalBuilderHref,
  stepId,
  update,
  validationErrors,
  withdrawalSettingsHref,
  withdrawalsFlowSettingsHref,
  withdrawalPortalLink,
}: {
  billingChecked: boolean;
  billingCheckPending: boolean;
  billingSettingsHref: string;
  form: FormState;
  merchant: MerchantType | null;
  onCheckBilling: () => void;
  onSelectMerchant: (merchant: MerchantType) => void;
  notificationSettingsHref: string;
  returnAddressOptions: WithdrawalsOnboardingAddressOption[];
  addressOptionsError: boolean;
  shippingSettingsHref: string;
  portalBuilderHref: string;
  stepId: StepId;
  update: (patch: Partial<FormState>) => void;
  validationErrors: ValidationErrors;
  withdrawalSettingsHref: string;
  withdrawalsFlowSettingsHref: string;
  withdrawalPortalLink: string;
}) {
  switch (stepId) {
    case "intro":
      return <IntroPage />;
    case "requirements":
      return <RequirementsPage />;
    case "how-redo-works":
      return <HowRedoWorksPage />;
    case "get-started":
      return <GetStartedPage onSelect={onSelectMerchant} />;
    case "account-setup":
      return (
        <AccountSetupPage
          addressOptionsError={addressOptionsError}
          billingChecked={billingChecked}
          billingCheckPending={billingCheckPending}
          billingSettingsHref={billingSettingsHref}
          form={form}
          notificationSettingsHref={notificationSettingsHref}
          onCheckBilling={onCheckBilling}
          returnAddressOptions={returnAddressOptions}
          shippingSettingsHref={shippingSettingsHref}
          update={update}
          validationErrors={validationErrors}
        />
      );
    case "go-live":
      return (
        <GoLivePage
          form={form}
          showCoverage={merchant === "new"}
          update={update}
          validationErrors={validationErrors}
          withdrawalPortalLink={withdrawalPortalLink}
          withdrawalsFlowSettingsHref={withdrawalsFlowSettingsHref}
        />
      );
    case "setup-go-live":
      return (
        <SetupGoLivePage
          form={form}
          update={update}
          validationErrors={validationErrors}
          withdrawalPortalLink={withdrawalPortalLink}
          withdrawalsFlowSettingsHref={withdrawalsFlowSettingsHref}
        />
      );
    case "confirmation":
      return (
        <ConfirmationPage
          form={form}
          merchant={merchant}
          portalBuilderHref={portalBuilderHref}
          withdrawalPortalLink={withdrawalPortalLink}
          withdrawalSettingsHref={withdrawalSettingsHref}
        />
      );
    default:
      assertNever(stepId);
  }
});

const IntroPage = memo(function IntroPage() {
  const { t } = useTranslation();
  return (
    <div className={styles.page}>
      <StepHeading
        intro={
          <T>
            Starting <strong>June 19, 2026</strong>, any business selling to EU
            consumers must offer a clear, digital way to withdraw from a
            purchase. If a shopper can buy in a few clicks, they must be able to
            withdraw in a few clicks, for a full refund, including shipping,
            duties, and taxes. Enforcement is expected, with stated penalties of
            up to 4% of annual turnover, so every brand selling to EU consumers
            needs a compliant withdrawal flow live before this date.
          </T>
        }
        title={t("The EU right of withdrawal, explained")}
      />
      <VimeoVideo
        label={t("Video: Understanding the EU right of withdrawal")}
        src={WITHDRAWALS_EXPLAINER_VIDEO_URL}
      />
      <Callout label={t("Legal disclaimer")}>
        {t(
          "This application does not constitute legal advice and is for educational purposes only. Each merchant is solely responsible for ensuring their own compliance with all applicable laws and regulations. Redo provides tooling to help facilitate compliance, but the obligation to comply rests entirely with the merchant. Confirm all compliance obligations with qualified legal counsel before taking action.",
        )}
      </Callout>
    </div>
  );
});

const RequirementsPage = memo(function RequirementsPage() {
  const { t } = useTranslation();
  return (
    <div className={styles.page}>
      <StepHeading
        intro={t(
          "Directive (EU) 2023/2673 amends the Consumer Rights Directive. At a high level, here is what you are required to provide:",
        )}
        title={t("What the directive requires")}
      />
      <div className={styles.section}>
        <SubHeading>{t("Withdrawal flow requirements")}</SubHeading>
        <CheckList
          items={[
            t("Provide a digital withdrawal function. Email is not sufficient"),
            t(
              "Make it easy to find; it cannot be hidden in a returns FAQ or account settings",
            ),
            t("No account creation required; order lookup is permitted"),
            t(
              "Use a two-step flow: submission first, then a separate confirmation step",
            ),
            t(
              "Send an automatic acknowledgement to the customer on a durable medium such as email",
            ),
          ]}
        />
      </div>
      <div className={styles.section}>
        <SubHeading>{t("Refund requirements")}</SubHeading>
        <CheckList
          items={[
            t(
              "Refund the standard outbound shipping the customer paid, up to your cheapest delivery option. Any express upgrade they chose does not need to be refunded",
            ),
            t(
              "If you ship DDP outside the EU, refund duties and taxes paid at checkout; DDU merchants are not responsible for these",
            ),
            t(
              "Customers have 14 days from delivery to submit a withdrawal, then 14 days from submission to return the goods",
            ),
            t(
              "For unfulfilled orders: refund within 14 days of the withdrawal submission",
            ),
            t(
              "For fulfilled orders: you may withhold the refund until proof of return is received or the goods arrive, whichever comes first",
            ),
            t(
              "If proof of return arrives before day 14: refund within the 14-day window",
            ),
            t("If proof of return arrives after day 14: refund immediately"),
          ]}
        />
      </div>
      <div className={styles.section}>
        <SubHeading>{t("Why it is harder than just a button")}</SubHeading>
        <NoteCard
          icon={ClockIcon}
          title={t("Processing returns before the clock runs out")}
        >
          <p className={styles.noteBodyText}>
            {t(
              "For cross-border orders, the 14-day refund clock can expire before a returned item is back in your hands and inspected. Routing EU returns to a local return center lets items be inspected in-region, so refunds stay both compliant and accurate.",
            )}
          </p>
        </NoteCard>
        <NoteCard icon={CoinsIcon} title={t("Who owes duties & taxes")}>
          <p className={styles.noteBodyText}>
            {t(
              "Your refund may include duties, taxes, and shipping. If the customer paid duties at checkout (DDP), those are refunded as part of the withdrawal. If they paid the carrier on delivery (DDU), they are not your responsibility. With a Merchant of Record, the duty and tax refund flows through them.",
            )}
          </p>
        </NoteCard>
        <NoteCard
          icon={ShieldIcon}
          title={t("Protecting your returns economics")}
        >
          <p className={styles.noteBodyText}>
            {t(
              "Keep withdrawal and returns as separate-but-connected paths: the withdrawal flow is deliberately frictionless, while your returns flow keeps gathering feedback and offering exchanges or store credit first, using the same backend and team UI.",
            )}
          </p>
          <p className={styles.noteBodyText}>
            {t(
              "The withdrawal flow does not allow shoppers to submit a withdrawal unless their order's shipping address is in the EU.",
            )}
          </p>
        </NoteCard>
      </div>
    </div>
  );
});

const HowRedoWorksPage = memo(function HowRedoWorksPage() {
  const { t } = useTranslation();
  return (
    <div className={styles.page}>
      <StepHeading
        intro={t(
          "Redo handles the full withdrawal experience for your shoppers and your team, and adapts to how far each order has progressed through fulfillment. Under the directive, you must issue a refund within 14 days of receiving the returned item; Redo's EU Return Center scans returned items on arrival and starts your refund clock immediately, so you stay compliant even before your team has physically reviewed the item.",
        )}
        title={t("From request to refund, handled")}
      />
      <div className={styles.section}>
        <SubHeading>
          {t("How withdrawals work by order fulfillment state:")}
        </SubHeading>
        <FulfillmentStates />
      </div>
    </div>
  );
});

const GetStartedPage = memo(function GetStartedPage({
  onSelect,
}: {
  onSelect: (merchant: MerchantType) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className={styles.compactPage}>
      <StepHeading
        intro={t("This helps us tailor your setup to exactly what you need.")}
        title={t("Are you new to Redo Returns?")}
      />
      <Flex dir="column" gap="xl" pt="2xl">
        <ChoiceCard
          description={t("Walk through the full setup, step by step.")}
          onClick={() => onSelect("new")}
          title={t("Yes, I am new to Redo Returns")}
        />
        <ChoiceCard
          description={t(
            "Skip account setup. Your shipping and billing are already configured.",
          )}
          onClick={() => onSelect("existing")}
          title={t("No, I am already using Redo Returns")}
        />
      </Flex>
    </div>
  );
});

const AccountSetupPage = memo(function AccountSetupPage({
  addressOptionsError,
  billingChecked,
  billingCheckPending,
  billingSettingsHref,
  form,
  notificationSettingsHref,
  onCheckBilling,
  returnAddressOptions,
  shippingSettingsHref,
  update,
  validationErrors,
}: {
  addressOptionsError: boolean;
  billingChecked: boolean;
  billingCheckPending: boolean;
  billingSettingsHref: string;
  form: FormState;
  notificationSettingsHref: string;
  onCheckBilling: () => void;
  returnAddressOptions: WithdrawalsOnboardingAddressOption[];
  shippingSettingsHref: string;
  update: (patch: Partial<FormState>) => void;
  validationErrors: ValidationErrors;
}) {
  const { t } = useTranslation();
  return (
    <div className={styles.page}>
      <StepHeading
        eyebrow={t("Step 1 of 2")}
        intro={t("Let's get your account configured for EU withdrawals.")}
        title={t("Account setup")}
      />
      <Flex dir="column" gap="xl">
        <ShippingSection
          addressOptionsError={addressOptionsError}
          form={form}
          returnAddressOptions={returnAddressOptions}
          settingsHref={shippingSettingsHref}
          update={update}
          validationError={validationErrors.returnAddress}
        />
        <BillingSection
          billingChecked={billingChecked}
          billingReady={form.billingReady}
          onCheckBilling={onCheckBilling}
          pending={billingCheckPending}
          settingsHref={billingSettingsHref}
        />
        <NotificationsSection
          form={form}
          settingsHref={notificationSettingsHref}
          update={update}
          validationError={validationErrors.notificationEmail}
        />
      </Flex>
    </div>
  );
});

const GoLivePage = memo(function GoLivePage({
  form,
  update,
  showCoverage,
  validationErrors,
  withdrawalsFlowSettingsHref,
  withdrawalPortalLink,
}: {
  form: FormState;
  update: (patch: Partial<FormState>) => void;
  showCoverage: boolean;
  validationErrors: ValidationErrors;
  withdrawalsFlowSettingsHref: string;
  withdrawalPortalLink: string;
}) {
  const { t } = useTranslation();
  return (
    <div className={styles.page}>
      <StepHeading
        eyebrow={t("Step 2 of 2")}
        intro={t(
          "These steps apply to every merchant. Complete them before you start accepting withdrawal requests.",
        )}
        title={t("Go live")}
      />
      <Flex dir="column" gap="xl">
        <PolicySection
          form={form}
          update={update}
          validationErrors={validationErrors}
          withdrawalPortalLink={withdrawalPortalLink}
          withdrawalsFlowSettingsHref={withdrawalsFlowSettingsHref}
        />
        <WithdrawalLinkSection withdrawalPortalLink={withdrawalPortalLink} />
        <FulfillmentNotifySection
          form={form}
          update={update}
          validationError={validationErrors.fulfillmentEmails}
        />
        {showCoverage && <CoverageCard />}
        <p className={styles.finalNote}>
          {t(
            "Once these steps are done, your store is ready to accept EU withdrawal requests. Remember: your brand remains the responsible trader. Confirm final label wording with your own legal counsel.",
          )}
        </p>
      </Flex>
    </div>
  );
});

const SetupGoLivePage = memo(function SetupGoLivePage({
  form,
  update,
  validationErrors,
  withdrawalsFlowSettingsHref,
  withdrawalPortalLink,
}: {
  form: FormState;
  update: (patch: Partial<FormState>) => void;
  validationErrors: ValidationErrors;
  withdrawalsFlowSettingsHref: string;
  withdrawalPortalLink: string;
}) {
  const { t } = useTranslation();
  return (
    <div className={styles.page}>
      <StepHeading
        eyebrow={t("Step 1 of 1")}
        intro={t(
          "You are already on Redo Returns, so your shipping and billing are configured. Configure your withdrawal flow and publish your policy to go live.",
        )}
        title={t("Set up and go live")}
      />
      <Flex dir="column" gap="xl">
        <PolicySection
          form={form}
          update={update}
          validationErrors={validationErrors}
          withdrawalPortalLink={withdrawalPortalLink}
          withdrawalsFlowSettingsHref={withdrawalsFlowSettingsHref}
        />
        <WithdrawalLinkSection withdrawalPortalLink={withdrawalPortalLink} />
        <FulfillmentNotifySection
          form={form}
          update={update}
          validationError={validationErrors.fulfillmentEmails}
        />
        <p className={styles.finalNote}>
          {t(
            "Once these steps are done, your store is ready to accept EU withdrawal requests. Remember: your brand remains the responsible trader. Confirm final label wording with your own legal counsel.",
          )}
        </p>
      </Flex>
    </div>
  );
});

const ConfirmationPage = memo(function ConfirmationPage({
  form,
  merchant,
  portalBuilderHref,
  withdrawalPortalLink,
  withdrawalSettingsHref,
}: {
  form: FormState;
  merchant: MerchantType | null;
  portalBuilderHref: string;
  withdrawalPortalLink: string;
  withdrawalSettingsHref: string;
}) {
  const { t } = useTranslation();
  const policyText = buildPolicy(form, withdrawalPortalLink);
  const steps = [
    {
      title: t("Add your withdrawal link to your policy page"),
      description: t(
        "Link out to your withdrawals login page so EU shoppers can start a withdrawal directly from your policy.",
      ),
      copy: { getText: () => withdrawalPortalLink, label: t("Copy link") },
    },
    {
      title: t("Update your FAQ with your right of withdrawal text"),
      description: t(
        "Copy the Right of Withdrawal text generated during setup into your published FAQ or return policy.",
      ),
      copy: { getText: () => policyText, label: t("Copy text") },
    },
    ...(merchant === "new"
      ? [
          {
            title: t("Customize your customer portal"),
            description: t(
              "New to Redo? Make the portal your own in the portal builder before you go live.",
            ),
            link: {
              href: portalBuilderHref,
              text: t("Open the portal builder"),
              external: true,
            },
          },
        ]
      : []),
  ];

  return (
    <div className={styles.page}>
      <StepHeading
        intro={t("Complete these last steps and you'll be compliant.")}
        title={t("Your withdrawals setup is almost complete")}
      />
      <div className={styles.section}>
        <NumberedSteps steps={steps} />
      </div>
      <div className={styles.section}>
        <SubHeading>{t("Redo is now automatically")}</SubHeading>
        <CheckList
          items={[
            t(
              "Providing a digital withdrawal function, since email alone is not sufficient",
            ),
            t(
              "Making it easy to find, never hidden in a returns FAQ or account settings",
            ),
            t(
              "Letting shoppers withdraw without creating an account, using order lookup",
            ),
            t(
              "Running a two-step flow: submission first, then a separate confirmation step",
            ),
          ]}
        />
      </div>
      <FieldCard
        description={t(
          "Customize your cancellation settings, add exclusions for customized products, or review and update your withdrawals configuration.",
        )}
        title={t("Manage your withdrawal settings")}
      >
        <ConfirmationFieldLink
          external
          href={withdrawalSettingsHref}
          text={t("View withdrawal settings")}
        />
      </FieldCard>
    </div>
  );
});
